import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  SimpleGrid,
  Switch,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import * as yup from 'yup';
import { getBoxesTemplates } from '../../../stores/slices/fourBoxesTemplates/fourBoxesTemplatesActions';
import {
  adminOverrideOrganisationBoxes,
  saveOrganisation,
} from '../../../stores/slices/organisations/organisationsActions';
import DeleteAlert from '../../Alerts/DeleteAlert';
import CustomEditor from './CustomEditor';

const OrganisationModal = ({ isOpen, onOpen, onClose, toUpdate }) => {
  const schema = yup
    .object({
      name: yup.string().required('Organisation name is required'),
    })
    .required();
  const { boxTemplates } = useSelector(state => state.boxTemplates);
  const dispatch = useDispatch();
  const toast = useToast();
  const [group1Tip, setGroup1Tip] = useState('');
  const [group2Tip, setGroup2Tip] = useState('');
  const [group3Tip, setGroup3Tip] = useState('');
  const [group4Tip, setGroup4Tip] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isOverriding, setIsOverriding] = useState(false);
  const cancelRef = useRef(null);
  const {
    isOpen: isOpenAlert,
    onOpen: onOpenAlert,
    onClose: onCloseAlert,
  } = useDisclosure();
  const {
    register,
    handleSubmit,
    reset,
    control,
    getValues,
    setValue,
    formState: { errors, isSubmitting },
    watch,
  } = useForm({
    resolver: yupResolver(schema),
  });

  const watchTemplate = watch('defaultTemplate');

  useEffect(() => {
    dispatch(getBoxesTemplates());
    // eslint-disable-next-line
  }, []);
  useEffect(() => {
    if (watchTemplate) {
      const template = boxTemplates.filter(
        template => template.id === parseInt(watchTemplate)
      )[0];
      setSelectedTemplate(template);
      setValue('group1Default', template.goal_category_templates[0].title);
      setValue('group2Default', template.goal_category_templates[1].title);
      setValue('group3Default', template.goal_category_templates[2].title);
      setValue('group4Default', template.goal_category_templates[3].title);
    }
  }, [watchTemplate]);

  const onSave = async data => {
    let orgData = {
      name: data.name,
      group_one_default: data.group1Default,
      group_two_default: data.group2Default,
      group_three_default: data.group3Default,
      group_four_default: data.group4Default,
      group_one_tip: group1Tip,
      group_two_tip: group2Tip,
      group_three_tip: group3Tip,
      group_four_tip: group4Tip,
      default_four_boxes_template_id: data.defaultTemplate,
      is_trial: data.is_trial
    };
    if (toUpdate.state) {
      orgData = { ...orgData, id: toUpdate.organisation.id };
    }
    const res = await dispatch(
      saveOrganisation({
        data: orgData,
        isUpdate: toUpdate,
      })
    );
    if (res.type === 'organisations/saveOrganisation/rejected') {
      toast({
        title: 'Something went wrong',
        description: 'Please try again later.',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'bottom-right',
      });
    } else {
      reset();
      onClose();
      toast({
        description: toUpdate.state
          ? 'Organisation has been updated'
          : 'Organisation has been created',
        colorScheme: 'primary',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'bottom-right',
        variant: 'primary',
      });
    }
  };

  const onOverride = async () => {
    setIsOverriding(true);
    const data = getValues();
    let orgData = {
      group_one_default: data.group1Default,
      group_two_default: data.group2Default,
      group_three_default: data.group3Default,
      group_four_default: data.group4Default,
      group_one_tip: group1Tip,
      group_two_tip: group2Tip,
      group_three_tip: group3Tip,
      group_four_tip: group4Tip,
      default_four_boxes_template_id: data.defaultTemplate,
      is_trial: data.is_trial
    };

    const res = await dispatch(
      adminOverrideOrganisationBoxes({
        id: toUpdate.organisation.id,
        data: orgData,
      })
    );
    setIsOverriding(false);
    onCloseAlert();
    if (res.type === 'organisations/adminOverrideOrganisationBoxes/rejected') {
      toast({
        title: 'Something went wrong',
        description: 'Please try again later.',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'bottom-right',
      });
    } else {
      toast({
        description: 'Organisation has been updated',
        colorScheme: 'primary',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'bottom-right',
        variant: 'primary',
      });
    }
  };

  return (
    <React.Fragment>
      <Modal
        closeOnOverlayClick={false}
        isOpen={isOpen}
        onClose={() => {
          reset();
          onClose();
        }}
      >
        <ModalOverlay />
        <ModalContent maxW={'6xl'}>
          <form onSubmit={handleSubmit(onSave)}>
            <ModalHeader>
              {toUpdate.state ? 'Edit' : 'Add new'} organisation
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody pb={6}>
              <VStack spacing={'2'}>
                <SimpleGrid
                  columns={{ base: 1, lg: 2 }}
                  spacingX={2}
                  spacingY={{ base: 2, lg: 2 }}
                  w={'full'}
                >
                  <FormControl id="name" isInvalid={errors.name}>
                    <FormLabel>Organisation Name</FormLabel>
                    <Input
                      id="name"
                      {...register('name')}
                      defaultValue={
                        toUpdate.state ? toUpdate.organisation.name : ''
                      }
                    />
                    <FormErrorMessage>
                      {errors.name && errors.name.message}
                    </FormErrorMessage>
                  </FormControl>
                  <FormControl
                    id="defaultTemplate"
                    isInvalid={errors.defaultTemplate}
                  >
                    <FormLabel>Default Template</FormLabel>
                    <Select
                      placeholder="Select option"
                      id="defaultTemplate"
                      {...register('defaultTemplate')}
                      defaultValue={
                        toUpdate.state
                          ? toUpdate.organisation.default_four_boxes_template_id
                          : ''
                      }
                    >
                      {boxTemplates && boxTemplates.length > 0 ? (
                        boxTemplates.map(template => (
                          <option key={template.id} value={template.id}>
                            {template.name}
                          </option>
                        ))
                      ) : (
                        <option value={''}>No template found</option>
                      )}
                    </Select>
                    <FormErrorMessage>
                      {errors.defaultTemplate && errors.defaultTemplate.message}
                    </FormErrorMessage>
                  </FormControl>
                  <VStack>
                    <FormControl
                      id="group1Default"
                      isInvalid={errors.group1Default}
                    >
                      <FormLabel>Goal group one tool tip</FormLabel>
                      <Input
                        id="group1Default"
                        {...register('group1Default')}
                        defaultValue={
                          selectedTemplate
                            ? selectedTemplate.goal_category_templates[0].title
                            : toUpdate.state
                            ? toUpdate.organisation.group_one_default
                            : ''
                        }
                      />
                      <FormErrorMessage>
                        {errors.group1Default && errors.group1Default.message}
                      </FormErrorMessage>
                    </FormControl>
                    <CustomEditor
                      onChange={setGroup1Tip}
                      val={
                        selectedTemplate
                          ? selectedTemplate.goal_category_templates[0].tool_tip
                          : toUpdate.state &&
                            toUpdate.organisation.group_one_tip
                          ? toUpdate.organisation.group_one_tip
                          : ''
                      }
                    />
                  </VStack>
                  <VStack>
                    <FormControl
                      id="group2Default"
                      isInvalid={errors.group2Default}
                    >
                      <FormLabel>Goal group two tool tip</FormLabel>
                      <Input
                        id="group2Default"
                        {...register('group2Default')}
                        defaultValue={
                          selectedTemplate
                            ? selectedTemplate.goal_category_templates[1].title
                            : toUpdate.state
                            ? toUpdate.organisation.group_two_default
                            : ''
                        }
                      />
                      <FormErrorMessage>
                        {errors.group2Default && errors.group2Default.message}
                      </FormErrorMessage>
                    </FormControl>
                    <CustomEditor
                      onChange={setGroup2Tip}
                      val={
                        selectedTemplate
                          ? selectedTemplate.goal_category_templates[1].tool_tip
                          : toUpdate.state &&
                            toUpdate.organisation.group_two_tip
                          ? toUpdate.organisation.group_two_tip
                          : ''
                      }
                    />
                  </VStack>
                  <VStack>
                    <FormControl
                      id="group3Default"
                      isInvalid={errors.group3Default}
                    >
                      <FormLabel>Goal group three tool tip</FormLabel>
                      <Input
                        id="group3Default"
                        {...register('group3Default')}
                        defaultValue={
                          selectedTemplate
                            ? selectedTemplate.goal_category_templates[2].title
                            : toUpdate.state
                            ? toUpdate.organisation.group_three_default
                            : ''
                        }
                      />
                      <FormErrorMessage>
                        {errors.group3Default && errors.group3Default.message}
                      </FormErrorMessage>
                    </FormControl>
                    <CustomEditor
                      onChange={setGroup3Tip}
                      val={
                        selectedTemplate
                          ? selectedTemplate.goal_category_templates[2].tool_tip
                          : toUpdate.state &&
                            toUpdate.organisation.group_three_tip
                          ? toUpdate.organisation.group_three_tip
                          : ''
                      }
                    />
                  </VStack>
                  <VStack>
                    <FormControl
                      id="group4Default"
                      isInvalid={errors.group4Default}
                    >
                      <FormLabel>Goal group four tool tip</FormLabel>
                      <Input
                        id="group4Default"
                        {...register('group4Default')}
                        defaultValue={
                          selectedTemplate
                            ? selectedTemplate.goal_category_templates[3].title
                            : toUpdate.state
                            ? toUpdate.organisation.group_four_default
                            : ''
                        }
                      />
                      <FormErrorMessage>
                        {errors.group4Default && errors.group4Default.message}
                      </FormErrorMessage>
                    </FormControl>
                    <CustomEditor
                      onChange={setGroup4Tip}
                      val={
                        selectedTemplate
                          ? selectedTemplate.goal_category_templates[3].tool_tip
                          : toUpdate.state &&
                            toUpdate.organisation.group_four_tip
                          ? toUpdate.organisation.group_four_tip
                          : ''
                      }
                    />
                  </VStack>
                </SimpleGrid>
              </VStack>
            </ModalBody>
            <ModalFooter>
              <FormControl
                id="is_trial"
                isInvalid={errors.is_trial}
              >
                <FormLabel mb="0">Active Trial</FormLabel>
                <Switch
                  id="is_trial"
                  {...register('is_trial')}
                  defaultChecked={toUpdate.state ? toUpdate.organisation.is_trial : ''}
                />

                <FormErrorMessage>
                  {errors.is_trial && errors.is_trial.message}
                </FormErrorMessage>
              </FormControl>
              {toUpdate.state ? (
                <Button
                  type="button"
                  mr={3}
                  bg={'red.400'}
                  _hover={{
                    color: 'red.400',
                    bg: 'transparent',
                  }}
                  onClick={onOpenAlert}
                >
                  Override 4Boxes
                </Button>
              ) : null}
              <Button mr={3} type="submit" isLoading={isSubmitting}>
                Save
              </Button>
              <Button
                onClick={() => {
                  reset();
                  onClose();
                }}
                variant="outline"
                disabled={isSubmitting}
              >
                Cancel
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>
      {isOpenAlert && (
        <DeleteAlert
          isOpen={isOpenAlert}
          cancelRef={cancelRef}
          onClose={onCloseAlert}
          isDeleting={isOverriding}
          header={`Override 4Boxes?`}
          body={
            "Are you sure? This will override the category and tooltip on all of this organisation's employees."
          }
          onDeleteItem={onOverride}
          deleteText={'Override'}
        />
      )}
    </React.Fragment>
  );
};

export default OrganisationModal;
